import { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MOROCCO_CENTERS } from '../config/mapboxConfig';
import { mapboxMapHideAttributionClass } from '../utils/mapboxRtl';
import { acquireSharedMap, onSharedMapReady, releaseSharedMap } from '../utils/sharedMapInstance';
import { getMarkerIcon } from '../utils/markerIcons';
import logger from '@/utils/logger';

// Code couleur fleet
const STATUS_COLORS = {
  active_moving: '#22c55e', // vert        – trajet élèves, en mouvement
  active_stopped: '#eab308', // jaune       – trajet élèves, à l'arrêt
  parked_mid_trip: '#f59e0b', // ambre       – garé en plein trajet (anomalie)
  service_moving: '#06b6d4', // cyan        – service en mouvement
  free_movement: '#06b6d4', // cyan (alias) – mouvement sans trajet planifié → service
  service_stopped: '#22d3ee', // cyan       – service, bus à l'arrêt
  idle: '#67e8f9', // cyan clair – moteur allumé sans trajet actif
  parked: '#9ca3af', // gris        – moteur coupé
  offline: '#ef4444', // rouge       – aucun signal > 15 min
};

const STATUS_LABELS = {
  active_moving: 'En trajet · En mouvement',
  active_stopped: "En trajet · À l'arrêt",
  parked_mid_trip: 'Garé · Trajet en cours',
  service_moving: 'Service · En mouvement',
  free_movement: 'Service · En mouvement',
  service_stopped: "Service · À l'arrêt",
  idle: 'Service · Moteur allumé',
  parked: 'Garé · Moteur coupé',
  offline: 'Hors ligne',
};

// Entrées affichées dans la légende carte (free_movement est un alias de service_moving, exclu)
const LEGEND_ENTRIES = [
  'active_moving',
  'active_stopped',
  'parked_mid_trip',
  'service_moving',
  'service_stopped',
  'idle',
  'parked',
  'offline',
];

function getMarkerKey(bus) {
  return bus.derivedStatus ?? 'offline';
}

function getMarkerColor(bus) {
  return STATUS_COLORS[getMarkerKey(bus)] ?? STATUS_COLORS.offline;
}

// Même SVG vue-de-dessus que SVG_FALLBACKS.bus dans markerIcons.js
function createBusSvgUrl(color) {
  const svg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="2" width="12" height="20" rx="2" fill="rgba(0,0,0,0.3)" transform="translate(0, 1)"/><rect x="6" y="2" width="12" height="20" rx="2" fill="${color}"/><rect x="7" y="3" width="10" height="3" rx="1" fill="#1E293B"/><rect x="7" y="19" width="10" height="2" rx="0.5" fill="#1E293B"/><rect x="7" y="7" width="10" height="11" rx="1" fill="rgba(255,255,255,0.2)"/><rect x="9" y="14" width="6" height="3" rx="0.5" fill="rgba(255,255,255,0.4)"/><path d="M12 8L15 12H9L12 8Z" fill="white" opacity="0.9"/></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function buildPopupHTML(bus, color) {
  const speed = bus.position?.speed != null ? `${Math.round(bus.position.speed)} km/h` : '—';
  const statusKey = getMarkerKey(bus);
  const label = STATUS_LABELS[statusKey];
  const lastSeen = bus.lastSeen
    ? new Date(bus.lastSeen).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
  const isBuffered = bus.position?.buffered === true;
  return `
    <div style="padding:10px;min-width:190px;font-family:system-ui,sans-serif">
      <div style="font-weight:700;font-size:14px;margin-bottom:6px">🚌 Bus ${bus.busNumber || '—'}</div>
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
        <span style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0"></span>
        <span style="font-size:12px;color:#475569">${label}</span>
      </div>
      ${isBuffered ? `<div style="font-size:11px;color:#f59e0b;margin-bottom:4px">⚠️ Position en différé (bufférisée)</div>` : ''}
      ${bus.route ? `<div style="font-size:12px;color:#64748b;margin-bottom:2px">Circuit : <strong>${bus.route.name}</strong></div>` : ''}
      ${bus.driver ? `<div style="font-size:12px;color:#64748b;margin-bottom:2px">Chauffeur : <strong>${bus.driver.name}</strong></div>` : ''}
      <div style="font-size:12px;color:#64748b">Vitesse : <strong>${speed}</strong></div>
      <div style="font-size:11px;color:#94a3b8;margin-top:4px">Dernier signal : ${lastSeen}</div>
    </div>`;
}

function LegendPanel() {
  const [open, setOpen] = useState(true);
  return (
    <div className="hidden md:block" style={{ position: 'absolute', bottom: 24, left: 12, zIndex: 10 }}>
      <div
        style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontSize: 11,
          overflow: 'hidden',
        }}
      >
        {/* En-tête cliquable */}
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '6px 10px',
            gap: 20,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#374151',
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          <span>Légende</span>
          <span
            style={{
              fontSize: 9,
              color: '#9ca3af',
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            ▼
          </span>
        </button>

        {/* Corps pliable */}
        {open && (
          <div style={{ padding: '0 10px 8px', lineHeight: 1.8 }}>
            {LEGEND_ENTRIES.map((key) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: STATUS_COLORS[key],
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: '#475569' }}>{STATUS_LABELS[key]}</span>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 4,
                borderTop: '1px solid #e2e8f0',
                paddingTop: 4,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  border: '2px solid #f59e0b',
                  background: 'transparent',
                  flexShrink: 0,
                }}
              />
              <span style={{ color: '#92400e' }}>Position en différé</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Carte Mapbox GL pour le suivi flotte 24/7.
 * Pattern impératif : même approche que InteractiveMap.jsx.
 * Les marqueurs sont mis à jour via setLngLat() sans re-mount.
 */
const FleetMap = ({
  buses = [],
  school = null,
  selectedBusId = null,
  focusTrigger = 0,
  onBusClick,
  style = {},
  height = '100%',
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersById = useRef({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const schoolCenteredRef = useRef(false);

  // Initialisation de la carte — instance Mapbox partagée (reuseMap),
  // pas de map load supplémentaire à chaque montage. Voir utils/sharedMapInstance.js.
  useEffect(() => {
    if (map.current) return;

    // Centre initial : école si dispo, sinon Casablanca
    const initCenter =
      school?.longitude && school?.latitude
        ? [parseFloat(school.longitude), parseFloat(school.latitude)]
        : [MOROCCO_CENTERS.casablanca.lng, MOROCCO_CENTERS.casablanca.lat];

    const owner = {};
    map.current = acquireSharedMap(mapContainer.current, { center: initCenter, zoom: 13 }, owner);

    let cancelled = false;
    onSharedMapReady(() => {
      if (cancelled) return;
      map.current?.resize();
      setMapLoaded(true);
    });

    // ResizeObserver : re-calcule les dimensions si le container change (layout flex/grid)
    const ro = new ResizeObserver(() => {
      map.current?.resize();
    });
    if (mapContainer.current) ro.observe(mapContainer.current);

    return () => {
      cancelled = true;
      ro.disconnect();
      Object.values(markersById.current).forEach((m) => {
        try {
          m.remove();
        } catch (_) {
          /* cleanup */
        }
      });
      markersById.current = {};
      schoolCenteredRef.current = false;
      releaseSharedMap(owner);
      map.current = null;
      if (!cancelled) setMapLoaded(false);
    };
  }, []);

  // Centrage sur l'école dès que la carte est prête (si pas encore centré)
  useEffect(() => {
    if (!mapLoaded || !map.current || schoolCenteredRef.current) return;
    if (!school?.longitude || !school?.latitude) return;
    map.current.flyTo({
      center: [parseFloat(school.longitude), parseFloat(school.latitude)],
      zoom: 13,
      duration: 800,
    });
    schoolCenteredRef.current = true;
  }, [mapLoaded, school]);

  // Mise à jour des marqueurs à chaque changement de buses
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    const currentIds = new Set();

    // ── Marker école ──────────────────────────────────────────────────────────
    if (school?.latitude && school?.longitude) {
      const schoolId = 'school-main';
      currentIds.add(schoolId);
      if (!markersById.current[schoolId]) {
        const iconUrl = getMarkerIcon('school');
        const el = document.createElement('div');
        el.className = 'custom-school-marker';
        el.style.width = '48px';
        el.style.height = '48px';
        el.style.cursor = 'pointer';
        el.style.backgroundImage = `url('${iconUrl}')`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundPosition = 'center';

        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([parseFloat(school.longitude), parseFloat(school.latitude)])
          .setPopup(
            new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(
              `<div style="padding:8px;font-family:system-ui,sans-serif">
                <div style="font-weight:700;font-size:13px;margin-bottom:2px">🏫 ${school.name || 'École'}</div>
                <div style="font-size:11px;color:#64748b">École</div>
              </div>`,
            ),
          )
          .addTo(map.current);
        markersById.current[schoolId] = marker;
        logger.debug('🏫 [FleetMap] Marker école ajouté');
      }
    }

    buses.forEach((bus) => {
      if (!bus.position?.latitude || !bus.position?.longitude) return;

      const id = `bus-${bus.busId}`;
      currentIds.add(id);

      const color = getMarkerColor(bus);
      const isSelected = bus.busId === selectedBusId;
      const svgUrl = createBusSvgUrl(color);

      if (markersById.current[id]) {
        // Mise à jour impérative — aucun objet Mapbox recréé
        const marker = markersById.current[id];
        marker.setLngLat([bus.position.longitude, bus.position.latitude]);
        if (bus.position.heading != null) marker.setRotation(bus.position.heading);

        const el = marker.getElement();
        el.style.backgroundImage = `url("${svgUrl}")`;
        el.style.filter = isSelected
          ? 'drop-shadow(0 0 4px rgba(0,0,0,0.6))'
          : 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))';

        // Ne reconstruire le HTML du popup que s'il est ouvert (évite les mutations DOM inutiles)
        const existingPopup = marker.getPopup();
        if (existingPopup?.isOpen()) {
          existingPopup.setHTML(buildPopupHTML(bus, color));
        }
      } else {
        // Création initiale — popup instancié une seule fois par marqueur
        const popup = new mapboxgl.Popup({
          offset: 32,
          closeButton: false,
        }).setHTML(buildPopupHTML(bus, color));

        const el = document.createElement('div');
        el.className = 'fleet-bus-marker';
        el.style.width = '40px';
        el.style.height = '40px';
        el.style.cursor = 'pointer';
        el.style.backgroundImage = `url("${svgUrl}")`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundPosition = 'center';
        el.style.filter = isSelected
          ? 'drop-shadow(0 0 4px rgba(0,0,0,0.6))'
          : 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))';
        el.title = `Bus ${bus.busNumber || ''}`;

        el.addEventListener('click', () => {
          if (onBusClick) onBusClick(bus);
        });

        // rotationAlignment: 'map' → l'icône tourne solidairement avec la carte
        // pour que le cap affiché corresponde toujours au nord de la carte
        const markerOptions = {
          element: el,
          anchor: 'center',
          rotationAlignment: 'map',
        };
        if (bus.position.heading != null) {
          markerOptions.rotation = bus.position.heading;
        }

        const marker = new mapboxgl.Marker(markerOptions)
          .setLngLat([bus.position.longitude, bus.position.latitude])
          .setPopup(popup)
          .addTo(map.current);

        markersById.current[id] = marker;
        logger.debug(`📍 [FleetMap] Marqueur SVG créé: ${id}`);
      }
    });

    // Supprimer les marqueurs de bus qui ne sont plus dans la liste
    Object.keys(markersById.current).forEach((id) => {
      if (!currentIds.has(id)) {
        try {
          markersById.current[id].remove();
        } catch (_) {
          /* cleanup */
        }
        delete markersById.current[id];
      }
    });
  }, [buses, mapLoaded, selectedBusId, onBusClick]);

  // Recentrer sur le bus sélectionné — uniquement au changement de sélection,
  // pas à chaque mise à jour GPS (évite l'animation permanente toutes les secondes).
  const busesRef = useRef(buses);
  useEffect(() => {
    busesRef.current = buses;
  }, [buses]);

  useEffect(() => {
    if (!mapLoaded || !selectedBusId || !map.current) return;
    const bus = busesRef.current.find((b) => b.busId === selectedBusId);
    if (bus?.position?.latitude && bus?.position?.longitude) {
      map.current.flyTo({
        center: [bus.position.longitude, bus.position.latitude],
        zoom: 14,
        duration: 800,
      });
    }
    // buses intentionnellement absent des dépendances : on ne fly qu'à la sélection ou au Focus
  }, [selectedBusId, mapLoaded, focusTrigger]);

  return (
    <div style={{ position: 'relative', width: '100%', height, ...style }}>
      <div ref={mapContainer} className={mapboxMapHideAttributionClass} style={{ width: '100%', height: '100%' }} />

      {/* Légende pliable — masquée sur mobile */}
      <LegendPanel />
    </div>
  );
};

export default FleetMap;
